import { Card, CardProps, Stack, StackProps, Typography } from '@mui/material';
import { mixinSx } from '@watershed/style/styleUtils';
import { forwardRef, ReactNode } from 'react';

function Well({ sx, ...props }: StackProps) {
  return (
    <Stack
      component="section"
      {...props}
      className="UIWell"
      sx={mixinSx(
        {
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.grey10,
          borderRadius: '10px',
          padding: '4px',
        },
        sx
      )}
    />
  );
}

export default Well;

Well.Header = function WellHeader({
  title,
  subtitle,
  titleLevel = 2,
  titleVariant = 'subtitle1',
  titleColor = 'secondary',
  actions,
  sx,
}: Pick<StackProps, 'sx'> & {
  title: ReactNode;
  subtitle?: ReactNode;
  titleLevel?: 2 | 3 | 4;
  titleVariant?: 'h2' | 'h3' | 'h4' | 'subtitle1' | 'subtitle2';
  titleColor?: 'primary' | 'secondary';
  actions?: ReactNode;
}) {
  const component = `h${titleLevel}` as keyof JSX.IntrinsicElements;
  return (
    <Stack
      component="header"
      direction="row"
      justifyContent="space-between"
      className="UIWellHeader"
      sx={mixinSx(
        {
          gap: 1.5,
          padding: 1.5,
          alignItems: 'center',
          posiiton: 'relative',
        },
        sx
      )}
    >
      <Stack>
        <Typography
          variant={titleVariant}
          component={component}
          color={(theme) => theme.palette.text[titleColor]}
          sx={{ textWrap: 'balance' }}
          children={title}
        />
        {subtitle && (
          <Typography
            variant="body2"
            sx={{ textWrap: 'balance' }}
            children={subtitle}
          />
        )}
      </Stack>
      {actions && (
        <Stack direction="row" gap={1} alignItems="center">
          {actions}
        </Stack>
      )}
    </Stack>
  );
};

Well.Content = forwardRef<HTMLDivElement, CardProps>(function WellContent(
  { children, sx, ...props }: Omit<CardProps, 'className'>,
  ref
) {
  return (
    <Card
      component="article"
      {...props}
      ref={ref}
      className="UIWellContent"
      sx={mixinSx(
        {
          padding: 1.5,
          borderRadius: 1,
          position: 'relative',
        },
        sx
      )}
    >
      {children}
    </Card>
  );
});
